import type { SxProps, Theme } from '@mui/material/styles';
import {
  APP_BAR_TOOLBAR_MIN_HEIGHT,
  DRAWER_NAV_LOGO_HEIGHT_PX,
} from './appLayoutConstants';
import { isAltDev, isLocalDev, isStaging } from '../../themeEnvironment';

export const matchNavPath = (pathname: string, base: string): boolean =>
  pathname === base || pathname.startsWith(`${base}/`);

/** Matches the gold in `public/endchess-logo.png`. */
export const BRAND_GOLD = '#e8ad3a';

/** Icon beside a top-nav tab label — gold when that tab is active. */
export const navTabIconSx = (active: boolean): SxProps<Theme> =>
  active ? { color: BRAND_GOLD } : {};
const navTabTypographySx = {
  '& .MuiTypography-root': {
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 1.35,
  },
} as const;

/** Active top-nav tab: gold pill on the dark AppBar. */
export const navTabSx = (active: boolean): SxProps<Theme> => ({
  minWidth: 'auto',
  whiteSpace: 'nowrap',
  touchAction: 'manipulation',
  WebkitTapHighlightColor: 'transparent',
  borderRadius: 1,
  px: { lg: 0.5, xl: 0.875, xxl: 1.75, xxxl: 2 },
  py: { lg: 0.25, xl: 0.5, xxl: 1, xxxl: 1.25 },
  fontSize: { lg: '0.75rem', xl: '0.875rem', xxl: '1.1875rem', xxxl: '1.375rem' },
  ...navTabTypographySx,
  ...(active
    ? {
        bgcolor: BRAND_GOLD,
        color: 'grey.900',
        fontWeight: 600,
        '&:hover': {
          bgcolor: '#f0b84a',
        },
      }
    : {}),
});

/** Leading icon beside a desktop nav tab label — rem tracks App.css root scaling on 4K. */
export const navTabLeadingIconSx = (active: boolean): SxProps<Theme> => ({
  fontSize: { lg: '1rem', xl: '1.125rem', xxl: '1.75rem', xxxl: '2rem' },
  ...navTabIconSx(active),
});

/** Keeps a nav tab icon and label together as one flex item. */
export const navTabGroupSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: { lg: 0.125, xl: 0.375, xxl: 0.75, xxxl: 0.875 },
};

/** Trailing icon-only nav control — rem tracks App.css root scaling on 4K. */
export const navTabIconButtonSx = (active: boolean): SxProps<Theme> => ({
  ...navTabSx(active),
  minWidth: { lg: 32, xl: 36, xxl: 44, xxxl: 48 },
  px: { lg: 0.375, xl: 0.625, xxl: 1, xxxl: 1.25 },
  '& .MuiSvgIcon-root': {
    fontSize: { lg: '1rem', xl: '1.125rem', xxl: '1.75rem', xxxl: '2rem' },
  },
});

export const navSectionDividerSx: SxProps<Theme> = {
  mx: { xs: 1, sm: 2, lg: 0.25, xl: 0.5, xxl: 2.5, xxxl: 3 },
  borderRightWidth: 2,
};

const navBrandLogoEnvironmentBorder = (color: string): SxProps<Theme> => ({
  border: `2px solid ${color}`,
  borderRadius: '4px',
  boxSizing: 'border-box',
});

/** Build-time environment cue on the nav logo (local dev: red, alt-local: white, staging: grey). */
export const navBrandLogoEnvironmentBorderSx: SxProps<Theme> = isAltDev
  ? navBrandLogoEnvironmentBorder('#ffffff')
  : isStaging
    ? navBrandLogoEnvironmentBorder('#9e9e9e')
    : isLocalDev
      ? navBrandLogoEnvironmentBorder('#f44336')
      : {};

export const navBrandLogoSx: SxProps<Theme> = {
  display: 'block',
  height: { xs: 32, sm: 44, lg: 32, xl: 44, xxl: 56, xxxl: 64 },
  width: 'auto',
  maxWidth: '100%',
  objectFit: 'contain',
  ...navBrandLogoEnvironmentBorderSx,
};

export const drawerBrandLogoSx: SxProps<Theme> = {
  display: 'block',
  height: DRAWER_NAV_LOGO_HEIGHT_PX,
  width: 'auto',
  maxWidth: '100%',
  objectFit: 'contain',
  ...navBrandLogoEnvironmentBorderSx,
};

export const navToolbarSx: SxProps<Theme> = {
  width: '100%',
  maxWidth: '100%',
  minHeight: APP_BAR_TOOLBAR_MIN_HEIGHT,
  height: 'auto',
  minWidth: 0,
  px: { xs: 0.5, sm: 2, lg: 1, xl: 1.5, xxl: 3.5, xxxl: 4 },
  py: { xs: 0.5, sm: 0, lg: 0.25, xl: 0.5, xxl: 0.75 },
  display: 'flex',
  flexWrap: { xs: 'wrap', lg: 'nowrap' },
  alignItems: 'center',
  justifyContent: 'space-between',
  rowGap: { lg: 0.5, xxl: 0.75 },
  boxSizing: 'border-box',
};

export const navMenuItemsSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: { xs: 'wrap', lg: 'nowrap' },
  flex: { lg: 1 },
  minWidth: 0,
  justifyContent: { lg: 'flex-start', xl: 'space-evenly' },
  gap: { xs: 0.5, sm: 1, lg: 0.125, xl: 0.375, xxl: 1.5, xxxl: 2 },
};

export const navReviewBadgeSx: SxProps<Theme> = {
  '& .MuiBadge-badge': {
    fontSize: { lg: '0.625rem', xl: '0.75rem', xxl: '0.9375rem', xxxl: '1rem' },
    minWidth: { lg: 16, xl: 20, xxl: 26, xxxl: 28 },
    height: { lg: 16, xl: 20, xxl: 26, xxxl: 28 },
    fontWeight: 700,
  },
};
